import { search as searchApi } from '../api/search'
import type { SearchGroupResponse, SearchHitResponse, SearchScanScope } from '../api/search'
import { ApiError } from '../api/problem'
import { CASE_KIND_LABEL, label } from './labels'
import { TASK_STATUS_LABEL, type TaskStatus } from '../types/task'

/**
 * Global Search (top-nav, IncentiWise IA) — IB-27 S1 (ADR-0058) cuts this over from the prior
 * client-side federated implementation (6 parallel per-entity list calls, merged in the browser)
 * to a single `GET /api/v1/search` call against the new backend aggregator
 * (`module.uxplatform.internal.service.GlobalSearchService`), which is a direct backend port of
 * the exact same federation logic this file used to contain. The public contract — {@link
 * runGlobalSearch}'s signature, {@link SearchGroupResult}/{@link SearchResultItem} shapes, {@link
 * RESULT_CAP}/{@link SCAN_LIMIT} — is UNCHANGED, so `GlobalSearchOverlay.tsx` needed zero edits
 * (design §5, "behavior-invisible cutover").
 *
 * This file is now a thin transform: it (a) builds each hit's `href` (routing stays frontend-only,
 * design §4.1 — the backend has no knowledge of frontend routes) and (b) re-applies the i18n
 * `label()` lookups the backend deliberately does NOT perform server-side (it emits RAW enum wire
 * values inside `secondary` — e.g. a Case's raw `kind`/`overallStatus`, a Legal entity's raw
 * `jurisdiction`, a Task's raw `status` — precisely where this file used to call `label(...)`
 * itself). {@link translateSecondary} reconstructs the exact same final display string the old
 * client-side federation produced, deterministically, per `kind` — see its own doc comment.
 */

export type SearchEntityKind = 'account' | 'legalEntity' | 'contact' | 'branch' | 'case' | 'production' | 'task'

/** Top N results rendered per group — always disclosed in the UI, never a silent truncation.
 * Mirrors {@code GlobalSearchService.RESULT_CAP} server-side. */
export const RESULT_CAP = 5

/** The bounded scan window for entities with no server-side text filter (cases/tasks). Mirrors
 * {@code GlobalSearchService.SCAN_LIMIT} server-side. */
export const SCAN_LIMIT = 200

export interface SearchResultItem {
  kind: SearchEntityKind
  id: string
  /** Business label (name/title/derived reference) — never a raw UUID. */
  label: string
  /** A short "context" line — parent Account/Legal entity, status, etc. — fully translated. */
  secondary: string | null
  href: string
}

export interface SearchGroupResult {
  kind: SearchEntityKind
  groupLabel: string
  items: SearchResultItem[]
  /** True when there are known/likely additional matches beyond {@link RESULT_CAP}. */
  hasMore: boolean
  /** `'all'` = the server-side filter reached every matching tenant row; `'recent'` = only the
   * {@link SCAN_LIMIT} most-recently-created rows were searched. */
  scanScope: 'all' | 'recent'
  /** Set when this group's own fetch failed — isolates the failure so the other groups still render. */
  error: string | null
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.problem.title ?? err.message
  return 'Could not load results for this type.'
}

/** Routing stays entirely frontend-only (design §4.1) — the backend never computes a route. `task`
 * needs the linked Case's id (carried in {@link SearchHitResponse.parentId}, not the hit's own
 * `id`) to choose between the Case Tasks-tab route and "My Work". */
function buildHref(kind: SearchEntityKind, hit: SearchHitResponse): string {
  switch (kind) {
    case 'account':
      return `/clients/accounts/${hit.id}`
    case 'legalEntity':
      return `/clients/legal-entities/${hit.id}`
    case 'contact':
      return `/clients/contacts/${hit.id}`
    case 'branch':
      return `/clients/branches/${hit.id}`
    case 'case':
      return `/cases/${hit.id}`
    case 'production':
      return `/productions/${hit.id}`
    case 'task':
      return hit.parentId ? `/cases/${hit.parentId}/tasks` : '/my-work'
  }
}

const TASK_UNLINKED_SUFFIX = ' · Not linked to a case'

/**
 * Reconstructs the exact final display string the pre-cutover client-side federation produced,
 * from the backend's RAW-enum-value `secondary` (design §4.1's routing/i18n layering decision).
 * Deterministic per `kind` because the backend composition is fixed and known here:
 *  - `legalEntity`: `[accountName, jurisdiction].filter(nonBlank).join(' · ')` — the LAST segment
 *    (if any) is the raw jurisdiction code; `label('jurisdiction', ...)` is safe to re-apply to it
 *    unconditionally because that dictionary falls back to the raw input on no match (never a
 *    mangled `humanize()` guess against an unrelated account name).
 *  - `case`: always exactly `${kind} · ${overallStatus}` (both columns are `NOT NULL`) — translate
 *    each segment independently.
 *  - `task`: always `${status}` with an optional, fixed, already-English `' · Not linked to a
 *    case'` suffix — strip it, translate the status, reappend.
 *  - everything else: passed through verbatim (the old client never translated it either).
 */
function translateSecondary(kind: SearchEntityKind, secondary: string | null): string | null {
  if (secondary == null) return null
  switch (kind) {
    case 'legalEntity': {
      const parts = secondary.split(' · ')
      const lastIndex = parts.length - 1
      parts[lastIndex] = label('jurisdiction', parts[lastIndex])
      return parts.join(' · ')
    }
    case 'case': {
      const [kindPart, statusPart] = secondary.split(' · ')
      return `${CASE_KIND_LABEL[kindPart ?? ''] ?? kindPart} · ${label('caseStatus', statusPart)}`
    }
    case 'task': {
      const hasSuffix = secondary.endsWith(TASK_UNLINKED_SUFFIX)
      const statusRaw = hasSuffix ? secondary.slice(0, -TASK_UNLINKED_SUFFIX.length) : secondary
      const translated = TASK_STATUS_LABEL[statusRaw as TaskStatus] ?? statusRaw
      return hasSuffix ? `${translated}${TASK_UNLINKED_SUFFIX}` : translated
    }
    default:
      return secondary
  }
}

function toResultItem(kind: SearchEntityKind, hit: SearchHitResponse): SearchResultItem {
  return {
    kind,
    id: hit.id,
    label: hit.label,
    secondary: translateSecondary(kind, hit.secondary),
    href: buildHref(kind, hit),
  }
}

function toGroupResult(group: SearchGroupResponse): SearchGroupResult {
  const kind = group.kind as SearchEntityKind
  return {
    kind,
    groupLabel: group.groupLabel,
    items: group.items.map((hit) => toResultItem(kind, hit)),
    hasMore: group.hasMore,
    scanScope: group.scanScope,
    error: group.error,
  }
}

/** The fixed group order/shape the overlay renders — used to synthesize an all-groups-errored
 * response if the single `/search` request itself fails outright (e.g. network error), so the
 * overlay still shows a per-group disclosed error instead of throwing. */
const FIXED_GROUPS: ReadonlyArray<{ kind: SearchEntityKind; groupLabel: string; scanScope: SearchScanScope }> = [
  { kind: 'account', groupLabel: 'Account', scanScope: 'all' },
  { kind: 'legalEntity', groupLabel: 'Legal entity', scanScope: 'all' },
  { kind: 'case', groupLabel: 'Case', scanScope: 'recent' },
  { kind: 'production', groupLabel: 'Production', scanScope: 'recent' },
  { kind: 'contact', groupLabel: 'Contact', scanScope: 'all' },
  { kind: 'branch', groupLabel: 'Branch', scanScope: 'all' },
  { kind: 'task', groupLabel: 'Task', scanScope: 'recent' },
]

/**
 * Fetches every group's results in a single backend call (previously N parallel client calls),
 * fixed group order preserved. Per-group failure isolation is now a SERVER-side concern
 * (`GlobalSearchService`); a failure of the request AS A WHOLE (e.g. the network, or the endpoint
 * itself erroring) still degrades gracefully here to every group disclosing the same error,
 * exactly as a total client-side failure would have looked before.
 */
export async function runGlobalSearch(rawTerm: string, signal: AbortSignal): Promise<SearchGroupResult[]> {
  const term = rawTerm.trim()
  if (!term) return []
  try {
    const groups = await searchApi(term, undefined, signal)
    return groups.map(toGroupResult)
  } catch (err) {
    const message = errorMessage(err)
    return FIXED_GROUPS.map((g) => ({
      kind: g.kind,
      groupLabel: g.groupLabel,
      items: [],
      hasMore: false,
      scanScope: g.scanScope,
      error: message,
    }))
  }
}
